/**
 * PlayerPreviewStagePickerView — the drawer's stage selector (SE-5494 AC4).
 *
 * Renders one button per stage, the current stage filled, everything else a
 * quiet outlined chip — the same affordance the reopen dialog uses for its
 * working-stage picker, so the pipeline has one visual vocabulary for "pick a
 * stage".
 *
 * ## Why this is still a chip row and not a dropdown (SE-6554)
 *
 * The restyle spec draws this control as an underline-style SELECT: the
 * current stage on a line with a chevron, the alternatives hidden until it is
 * opened. It stays a chip row because hiding the alternatives is a behaviour
 * change, not a paint change — `player-preview-drawer.spec.ts` and
 * `pipeline-stage-picker-protected.spec.ts` both click a stage option
 * DIRECTLY, which only works while every option is on screen, and collapsing
 * them would also move the open/close state into the container. The chips take
 * the spec's quieter treatment (short row height, muted unselected chrome);
 * converting the control itself is a product call.
 *
 * This component knows NOTHING about the move: it emits `onSelectStage(id)` and
 * the container hands that to the board's shared move service. That is what
 * makes AC4's "not a separate code path" true at the component boundary — there
 * is no mutation, no prompt and no gate logic anywhere in this file.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/PlayerPreviewStagePicker
 */

import { memo } from "react";

import { TEST_ID_PLAYER_PREVIEW_DRAWER } from "e2e/testId";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";

/** One selectable stage. */
export interface PlayerPreviewStageOption {
  /** Phase id. */
  readonly id: string;
  /** Phase display name. */
  readonly name: string;
}

/** Props for {@link PlayerPreviewStagePickerView}. */
export interface PlayerPreviewStagePickerViewProps {
  /** Stages the card can be moved into, in board order. */
  readonly stages: readonly PlayerPreviewStageOption[];
  /** The card's current stage id. */
  readonly currentStageId?: string;
  /** Whether the viewer may move this card. */
  readonly canEdit: boolean;
  /** Whether a move is in flight (disables the whole picker). */
  readonly isMoving: boolean;
  /**
   * Pre-bound press handler per stage id, built by the container.
   * @remarks
   * Supplied rather than derived here for two reasons: the repo's
   * `react-perf/jsx-no-new-function-as-prop` rule forbids inline closures in
   * JSX, and a View may hold no hooks of its own — so the memoised map is the
   * container's job.
   */
  readonly stageHandlers: Readonly<Record<string, () => void>>;
}

/**
 * Renders the stage selector.
 * @param props - {@link PlayerPreviewStagePickerViewProps}
 * @param props.stages - Stages the card can be moved into
 * @param props.currentStageId - The card's current stage id
 * @param props.canEdit - Whether the viewer may move this card
 * @param props.isMoving - Whether a move is in flight
 * @param props.stageHandlers - Pre-bound press handler per stage id
 * @returns The stage picker.
 */
const PlayerPreviewStagePickerView = ({
  stages,
  currentStageId,
  canEdit,
  isMoving,
  stageHandlers,
}: PlayerPreviewStagePickerViewProps) => (
  <Stack
    direction="horizontal"
    space="2"
    className="flex-wrap"
    testID={TEST_ID_PLAYER_PREVIEW_DRAWER.STAGE_SECTION}
  >
    {stages.map(stage => {
      const isCurrent = stage.id === currentStageId;
      return (
        <Button
          key={stage.id}
          testID={
            isCurrent
              ? TEST_ID_PLAYER_PREVIEW_DRAWER.CURRENT_STAGE
              : TEST_ID_PLAYER_PREVIEW_DRAWER.stageOptionFor(stage.id)
          }
          label={stage.name}
          accessibilityLabel={
            isCurrent ? `Current stage: ${stage.name}` : `Move to ${stage.name}`
          }
          variant={isCurrent ? "solid" : "outline"}
          tone={isCurrent ? "accent" : "secondary"}
          size="sm"
          className="h-9"
          // The current stage is not a move target; a disabled current button
          // also stops a redundant same-stage mutation being fired.
          isDisabled={isCurrent || !canEdit || isMoving}
          onPress={stageHandlers[stage.id]}
        />
      );
    })}
  </Stack>
);

PlayerPreviewStagePickerView.displayName = "PlayerPreviewStagePickerView";

export default memo(PlayerPreviewStagePickerView);
